import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  CircularProgress, 
  Alert, 
  Snackbar,
  Paper,
  TextField,
  Divider,
  IconButton,
  AlertTitle,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Elements } from '@stripe/react-stripe-js';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { 
  getMerchandise,
  createPaymentIntent,
  processPayment,
  confirmPaymentAndOrder
} from '../services/storeService';
import analyticsService from '../services/analyticsService';
import screamifyLogo from '../img/screamifyweblogo.png';

// Stripe publishable key
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51QHSHQCTqq6nTI6o0uOo2Ea6OO3zoYjFGZgY1JANteUTMdYzpWJPSwAG1NzKIv1ecTQFBSdUBMXyknxkP9iY3wXN00MXSTtWFX';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Styled components
const ProductImage = styled(CardMedia)(({ theme }) => ({
  height: 'auto',
  maxHeight: 450,
  backgroundSize: 'contain',
  transition: 'transform 0.4s cubic-bezier(0.25, 0.45, 0.45, 0.95)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.03)',
  },
  [theme.breakpoints.down('sm')]: {
    maxHeight: 280,
  },
}));

const ColorOption = styled(Box)(({ 
  theme, 
  color, 
  selected 
}) => ({
  width: 38,
  height: 38,
  borderRadius: '50%',
  backgroundColor: color,
  cursor: 'pointer',
  border: selected ? '2px solid #ff0000' : '2px solid transparent',
  boxShadow: selected ? '0 0 12px rgba(255, 0, 0, 0.6)' : 'none',
  margin: theme.spacing(0.5),
  transition: 'all 0.3s cubic-bezier(0.25, 0.45, 0.45, 0.95)',
  '&:hover': {
    transform: 'scale(1.15)',
    boxShadow: '0 0 10px rgba(255, 0, 0, 0.4)',
  },
}));

// Payment modal style
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 550 },
  bgcolor: 'background.paper',
  border: '2px solid #ff0000',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
  p: { xs: 2, sm: 4 },
  borderRadius: 3,
  maxHeight: '90vh',
  overflow: 'auto'
};

// Checkout form component using Stripe Elements
const CheckoutForm = ({ clientSecret, orderData, onPaymentComplete, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });
  
  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateCustomerInfo = () => {
    // Simple validation for required fields
    if (!customerInfo.fullName.trim()) return "Full name is required";
    if (!customerInfo.email.trim() || !customerInfo.email.includes('@')) return "Valid email address is required";
    if (!customerInfo.phone.trim()) return "Phone number is required";
    if (!customerInfo.address.trim()) return "Address is required";
    if (!customerInfo.city.trim()) return "City is required";
    if (!customerInfo.state.trim()) return "State is required";
    if (!customerInfo.zip.trim()) return "ZIP code is required";
    return null;
  };
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }
    
    // Validate customer info
    const validationError = validateCustomerInfo();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setProcessing(true);
    setError(null);
    
    try {
      // Get the payment element
      const paymentElement = elements.getElement(PaymentElement);
      
      if (!paymentElement) {
        throw new Error('Payment element not found');
      }
      
      // Confirm payment with Stripe
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: customerInfo.fullName,
              email: customerInfo.email,
              phone: customerInfo.phone,
              address: {
                line1: customerInfo.address,
                city: customerInfo.city,
                state: customerInfo.state,
                postal_code: customerInfo.zip,
                country: customerInfo.country
              }
            }
          }
        },
        redirect: 'if_required'
      });
      
      // Check for errors
      if (result.error) {
        setError(result.error.message);
      } else if (result.paymentIntent) {
        // Payment succeeded
        setMessage('Payment successful! Processing your order...');
        
        // Call the callback with payment result and customer info
        if (onPaymentComplete) {
          onPaymentComplete(result.paymentIntent, customerInfo);
        }
      } else {
        // Handle case where we don't have a direct paymentIntent response
        // This can happen if there's a redirect flow
        const clientSecretParam = new URLSearchParams(window.location.search).get(
          'payment_intent_client_secret'
        );
        
        if (clientSecretParam) {
          const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecretParam);
          if (paymentIntent && paymentIntent.status === 'succeeded') {
            setMessage('Payment successful! Processing your order...');
            onPaymentComplete(paymentIntent, customerInfo);
          } else {
            setError('Payment verification failed. Please try again.');
          }
        } else {
          setError('Something went wrong with your payment. Please try again.');
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred during payment processing');
    } finally {
      setProcessing(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          fontWeight: 600,
          color: '#212121',
          pb: 1,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          mb: 2.5
        }}
      >
        Shipping Information
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Full Name"
            name="fullName"
            value={customerInfo.fullName}
            onChange={handleCustomerInfoChange}
            variant="outlined"
            size="small"
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                padding: '12px 14px' 
              },
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff0000'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ff0000'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={customerInfo.email}
            onChange={handleCustomerInfoChange}
            variant="outlined"
            size="small"
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                padding: '12px 14px' 
              },
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff0000'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ff0000'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Phone Number"
            name="phone"
            value={customerInfo.phone}
            onChange={handleCustomerInfoChange}
            variant="outlined"
            size="small"
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                padding: '12px 14px' 
              },
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff0000'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ff0000'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Shipping Address"
            name="address"
            value={customerInfo.address}
            onChange={handleCustomerInfoChange}
            variant="outlined"
            size="small"
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                padding: '12px 14px' 
              },
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff0000'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ff0000'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="City"
            name="city"
            value={customerInfo.city}
            onChange={handleCustomerInfoChange}
            variant="outlined"
            size="small"
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                padding: '12px 14px' 
              },
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff0000'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ff0000'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <TextField
            required
            fullWidth
            label="State"
            name="state"
            value={customerInfo.state}
            onChange={handleCustomerInfoChange}
            variant="outlined"
            size="small"
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                padding: '12px 14px' 
              },
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff0000'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ff0000'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <TextField
            required
            fullWidth
            label="ZIP Code"
            name="zip"
            value={customerInfo.zip}
            onChange={handleCustomerInfoChange}
            variant="outlined"
            size="small"
            sx={{ 
              '& .MuiInputBase-input': { 
                fontSize: { xs: '0.9rem', sm: '1rem' },
                padding: '12px 14px' 
              },
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff0000'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#ff0000'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormControl fullWidth size="small" sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.3)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ff0000'
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#ff0000'
            },
            '& .MuiInputBase-input': { 
              fontSize: { xs: '0.9rem', sm: '1rem' },
              padding: '12px 14px' 
            }
          }}>
            <InputLabel>Country</InputLabel>
            <Select
              name="country"
              value={customerInfo.country}
              onChange={handleCustomerInfoChange}
              label="Country"
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              <MenuItem value="US">United States</MenuItem>
              <MenuItem value="CA">Canada</MenuItem>
              <MenuItem value="GB">United Kingdom</MenuItem>
              <MenuItem value="AU">Australia</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          fontWeight: 600,
          color: '#212121',
          pb: 1,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          mb: 2.5,
          mt: 2
        }}
      >
        Payment Details
      </Typography>
      
      <Box sx={{ 
        mb: 3,
        '.StripeElement': {
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(0, 0, 0, 0.3)'
          },
          '&.StripeElement--focus': {
            borderColor: '#ff0000',
            boxShadow: '0 0 0 1px rgba(255, 0, 0, 0.3)'
          }
        }
      }}>
        <PaymentElement options={{ 
          layout: { 
            type: 'tabs',
            defaultCollapsed: false,
            radios: false
          },
          fields: {
            billingDetails: {
              address: {
                country: 'never'
              }
            }
          },
          theme: {
            variables: {
              colorPrimary: '#ff0000',
              colorBackground: '#ffffff',
              colorText: '#30313d',
              colorDanger: '#df1b41',
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              borderRadius: '8px'
            },
            rules: {
              '.Tab': {
                border: '1px solid rgba(0, 0, 0, 0.12)',
                boxShadow: '0px 1px 3px rgba(50, 50, 93, 0.1)'
              }
            }
          }
        }} />
      </Box>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(211, 47, 47, 0.2)'
          }}
        >
          {error}
        </Alert>
      )}
      
      {message && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(76, 175, 80, 0.2)'
          }}
        >
          {message}
        </Alert>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mt: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Button 
          variant="outlined" 
          onClick={onCancel}
          disabled={processing}
          sx={{
            order: { xs: 2, sm: 1 },
            color: '#666',
            borderColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: 2,
            py: 1.2,
            px: 3,
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.5)',
              backgroundColor: 'rgba(0, 0, 0, 0.03)'
            }
          }}
        >
          Cancel
        </Button>
        
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || processing}
          sx={{
            bgcolor: '#ff0000',
            color: 'white',
            py: 1.2,
            px: 4,
            fontWeight: 600,
            fontSize: '1rem',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)',
            order: { xs: 1, sm: 2 },
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: '#d10000',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(255, 0, 0, 0.4)'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              right: '-50%',
              bottom: '-50%',
              background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.13) 77%, rgba(255, 255, 255, 0.5) 92%, rgba(255, 255, 255, 0.3) 100%)',
              opacity: 0,
              transform: 'rotate(30deg)',
              transition: 'opacity 0.4s ease',
            },
            '&:hover::after': {
              opacity: 1,
              animation: 'shine 1.5s ease',
            }
          }}
          startIcon={<PaymentIcon />}
        >
          {processing ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            `Pay ${orderData ? `$${orderData.totalPrice.toFixed(2)}` : ''}`
          )}
        </Button>
      </Box>

      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          textAlign: 'center', 
          mt: 3, 
          color: 'rgba(0, 0, 0, 0.5)',
          fontSize: '0.75rem' 
        }}
      >
        Your payment information is processed securely by Stripe. We do not store your credit card details.
      </Typography>
    </form>
  );
};

const Shop = () => {
  // State
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImage, setCurrentImage] = useState(null);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResponse, setOrderResponse] = useState(null);
  
  // Payment state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentOrderData, setPaymentOrderData] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  
  
  // Fetch product data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // getMerchandise now returns the hardcoded variants with correct availability
        const productData = await getMerchandise();
        
        // Apply FIXED pricing - $29.99 for everyone
        if (productData.variants && productData.variants.length > 0) {
          productData.variants.forEach(variant => {
            variant.price = 2999; // Set fixed price in cents
            // Remove member/non-member specific prices if they exist
            delete variant.memberPrice; 
            delete variant.nonMemberPrice;
            delete variant.originalPrice;
          });
        }
        
        setProduct(productData);
        
        // Track product view in Google Analytics
        if (productData) {
          analyticsService.trackEvent('view_item', {
            currency: 'USD',
            value: 29.99,
            items: [{
              item_id: productData.id,
              item_name: productData.title,
              item_brand: 'Screamify',
              item_category: 'Apparel',
              price: 29.99
            }]
          });
        }
        
        // Simple image preloading that won't break anything
        if (productData.images && productData.images.length > 0) {
          // Preload the first image immediately
          const firstImage = productData.images[0];
          if (firstImage && firstImage.src) {
            const img = new Image();
            img.src = firstImage.src;
          }
        }
        
        // Set default variant to the first AVAILABLE one (should be Charcoal/Black)
        if (productData.variants && productData.variants.length > 0) {
          const defaultVariant = productData.variants.find(v => v.is_available) || productData.variants[0];
          setSelectedVariant(defaultVariant);
          
          // Find color option for this default variant
          if (productData.options && productData.options.length > 0) {
            const colorOption = productData.options.find(o => o.name === 'Colors');
            if (colorOption && defaultVariant.options && defaultVariant.options.length > 0) {
              const colorId = defaultVariant.options[0];
              const selectedColorObj = colorOption.values.find(v => v.id === colorId);
              // Ensure the found color object actually exists before setting
              if (selectedColorObj) {
                  setSelectedColor(selectedColorObj);
              } else {
                  // Fallback: Select the first available color option if possible
                  const firstAvailableColorOption = colorOption.values.find(co => 
                      productData.variants.some(pv => pv.is_available && pv.options && pv.options[0] === co.id)
                  );
                  setSelectedColor(firstAvailableColorOption || (colorOption.values.length > 0 ? colorOption.values[0] : null));
              }
            }
          }
        }
        
        // Set default image (find image associated with the selected variant if possible)
        let initialImage = null;
        if (productData.images && productData.images.length > 0) {
           const currentSelectedVariant = selectedVariant || (productData.variants.find(v => v.is_available) || productData.variants[0]);
           const variantImage = productData.images.find(img => 
             img.variant_ids && currentSelectedVariant && img.variant_ids.includes(currentSelectedVariant.id) && img.is_default
           ) || productData.images.find(img => img.is_default) || productData.images[0];
          initialImage = variantImage.src;
          setCurrentImage(initialImage);
        }
        
        setLoading(false);
      } catch (error) {
        setSnackbarMessage('Error loading product data. Please try again later.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    
    fetchData();
  // No dependencies as this is a one-time initialization
  }, []);
  
  // NEW useEffect hook specifically for updating the image when selectedVariant changes
  useEffect(() => {
      if (product && product.images && product.images.length > 0 && selectedVariant) {
          const variantImage = product.images.find(img => 
              img.variant_ids && img.variant_ids.includes(selectedVariant.id) && img.is_default
          ) || product.images.find(img => img.is_default) || product.images[0];
          
          // Only update if the image source is different
          if (variantImage && variantImage.src !== currentImage) {
              setCurrentImage(variantImage.src);
          }
      }
  // This hook ONLY depends on the selectedVariant and the product data (images)
  }, [selectedVariant, product]);
  
  // Handle color selection
  const handleColorSelect = (colorOption) => {
    // Find variant that matches this color option ID
    if (!product || !product.variants) {
        return;
    }
    
    const matchingVariant = product.variants.find(v => 
      v.options && v.options.length > 0 && v.options[0] === colorOption.id
    );
    
    if (matchingVariant) {
      // Update state
      setSelectedColor(colorOption); // Set selected color state
      setSelectedVariant(matchingVariant); // Set selected variant state
      
      // Update image if there's a variant-specific image
      const variantImage = product.images.find(img => 
        img.variant_ids && img.variant_ids.includes(matchingVariant.id) && img.is_default
      );
      
      if (variantImage) {
         setCurrentImage(variantImage.src);
      } else {
           // Fallback to default image if no specific image found
           const defaultImg = product.images.find(img => img.is_default) || (product.images.length > 0 ? product.images[0] : null);
           if (defaultImg) {
               setCurrentImage(defaultImg.src);
           }
      }
    }
  };
  
  // Handle image selection
  const handleImageSelect = (image) => {
    setCurrentImage(image.src);
  };
  
  // Handle quantity change
  const handleQuantityChange = (value) => {
    // Ensure quantity is between 1 and 10
    const newValue = Math.max(1, Math.min(10, value));
    setQuantity(newValue);
  };
  
  // Initiate payment process
  const handleInitiatePayment = async () => {
    if (!selectedVariant) {
      setSnackbarMessage('Please select a product variant');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Check availability using the hardcoded variant data
    if (!selectedVariant.is_available) {
      setSnackbarMessage(`Sorry, the selected ${selectedVariant.title || 'variant'} is out of stock`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Use the fixed price
    const currentPrice = selectedVariant.price; // Now always 2999 cents
    
    setIsSubmitting(true);
    try {
      // No need for real-time availability check here, we trust the hardcoded data
      
      // Calculate subtotal and tax
      const subtotalCents = currentPrice * quantity;
      const taxRatePercent = 12.5;
      const taxCents = Math.round(subtotalCents * (taxRatePercent / 100));
      const totalCents = subtotalCents + taxCents;
      
      const orderData = {
        id: product?.id, // Use optional chaining
        title: product?.title,
        selectedVariant: {
          ...selectedVariant,
          price: currentPrice // Ensure we're using the correct price based on auth status
        },
        quantity,
        price: currentPrice / 100, // Convert cents to dollars for display
        subtotalCents: subtotalCents,
        taxCents: taxCents,
        taxRatePercent: taxRatePercent,
        totalCents: totalCents,
        totalPrice: totalCents / 100, // Total including tax in dollars
        isAuthenticated: true // Still useful to know, but doesn't affect price
      };
      
      // Track add_to_cart event in Google Analytics
      analyticsService.trackEvent('add_to_cart', {
        currency: 'USD',
        value: orderData.totalPrice,
        items: [{
          item_id: product?.id,
          item_name: product?.title,
          item_variant: selectedVariant.title || 'Default',
          item_brand: 'Screamify',
          item_category: 'Apparel',
          price: orderData.price,
          quantity: quantity
        }]
      });
      
      // Set the order data for use in the payment flow
      setPaymentOrderData(orderData);
      
      // Create payment intent through our Lambda
      const paymentIntentResult = await createPaymentIntent({
        ...orderData,
        price_cents: totalCents // Pass the total with tax to the backend
      });
      
      if (!paymentIntentResult.success || !paymentIntentResult.clientSecret) {
        throw new Error('Failed to create payment intent');
      }
      
      // Set client secret for Stripe Elements
      setClientSecret(paymentIntentResult.clientSecret);
      
      // Open payment modal
      setPaymentModalOpen(true);
    } catch (error) {
      setSnackbarMessage(`Error: ${error.message || 'Failed to initiate payment'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle payment completion
  const handlePaymentComplete = async (paymentIntent, customerInfo) => {
    setPaymentProcessing(true);
    
    try {
      // Check if payment intent exists and has the necessary ID
      if (!paymentIntent || !paymentIntent.id) {
        throw new Error('Invalid payment intent data received');
      }
      
      // Add customer info to the payment order data
      const orderWithCustomerInfo = {
        ...paymentOrderData,
        customerInfo
      };
      
      // Submit order to Printify (will check hardcoded availability again)
      const result = await confirmPaymentAndOrder({
        payment_intent: paymentIntent.id
      }, orderWithCustomerInfo);
      
      // Track purchase event in Google Analytics
      if (paymentOrderData) {
        analyticsService.trackPurchase(
          paymentIntent.id,
          paymentOrderData.totalPrice,
          [{
            item_id: product?.id,
            item_name: product?.title,
            item_variant: paymentOrderData.selectedVariant?.title || 'Default',
            item_brand: 'Screamify',
            item_category: 'Apparel',
            price: paymentOrderData.price,
            quantity: paymentOrderData.quantity
          }]
        );
        
        // Add custom purchase_hat event for specific tracking
        analyticsService.trackEvent('purchase_hat', {
          transaction_id: paymentIntent.id,
          value: paymentOrderData.totalPrice,
          currency: 'USD',
          item_name: product?.title,
          variant: paymentOrderData.selectedVariant?.title || 'Default',
          quantity: paymentOrderData.quantity
        });
      }
      
      setOrderResponse(result);
      setOrderPlaced(true);
      setPaymentCompleted(true);
      setSnackbarMessage('Order placed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Close payment modal
      setPaymentModalOpen(false);
    } catch (error) {
      setPaymentError(error.message || 'Error processing order');
      setSnackbarMessage(`Error: ${error.message || 'Failed to process order'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setPaymentProcessing(false);
    }
  };
  
  // Close payment modal
  const handleClosePaymentModal = () => {
    if (!paymentProcessing) {
      setPaymentModalOpen(false);
      setClientSecret('');
    }
  };
  
  // Format price from cents to dollars
  const formatPrice = (cents) => {
    // Add check for valid input
    if (typeof cents !== 'number' || isNaN(cents)) {
        return '$0.00';
    }
    return `$${(cents / 100).toFixed(2)}`;
  };
  
  // Close snackbar
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Helper to check availability directly from variant data
  const isVariantAvailable = (variantId) => {
    const variant = product?.variants?.find(v => v.id === variantId);
    return variant ? variant.is_available : false;
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a0000 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 70% 30%, rgba(255, 0, 0, 0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0
        }
      }}
    >
      {/* SEO Optimizations */}
      <HelmetProvider>
        <Helmet>
          <title>{product ? `${product.title} | Screamify™ Shop` : 'Official Horror Merchandise | Screamify™ Shop'}</title>
          <meta name="description" content="Shop official Screamify™ branded merchandise including hats, t-shirts, and clothing for the world's first indie horror streaming service. 100% of proceeds go toward financing independent horror films." />
          <meta name="keywords" content={`Screamify, horror merchandise, indie horror, ${product ? product.title.toLowerCase() : 'horror hats, horror t-shirts'}, horror clothing, support independent horror films, final girls finish first`} />
          
          {/* Enhanced Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Product",
              "name": product ? product.title : "Screamify™ Official Merchandise",
              "image": currentImage || (product?.images?.[0]?.src || ""),
              "description": "Official merchandise from the world's first indie horror streaming service. 100% of proceeds go toward financing independent horror films.",
              "brand": {
                "@type": "Brand",
                "name": "Screamify™"
              },
              "offers": {
                "@type": "Offer",
                "url": "https://www.screamify.com/shop",
                "priceCurrency": "USD",
                "price": product && selectedVariant ? (selectedVariant.price / 100).toFixed(2) : "29.99",
                "availability": product && selectedVariant && selectedVariant.is_available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
              },
              "category": "Apparel > Merchandise > Horror",
              "sku": product && selectedVariant ? selectedVariant.id : "",
              "mpn": product && selectedVariant ? `SCREAM-${selectedVariant.id}` : "",
              "itemCondition": "https://schema.org/NewCondition"
            })}
          </script>
          
          {/* Enhanced Open Graph/social tags for shop items */}
          <meta property="og:type" content="product" />
          <meta property="og:title" content={product ? `${product.title} | Screamify™ Shop` : 'Official Horror Merchandise | Screamify™ Shop'} />
          <meta property="og:description" content="Shop official Screamify™ branded merchandise including hats, t-shirts, and clothing for the world's first indie horror streaming service." />
          <meta property="og:url" content="https://www.screamify.com/shop" />
          <meta property="og:image" content={currentImage || (product?.images?.[0]?.src || "https://www.screamify.com/social-share.png")} />
          <meta property="product:price:amount" content={product && selectedVariant ? (selectedVariant.price / 100).toFixed(2) : "29.99"} />
          <meta property="product:price:currency" content="USD" />
          <meta property="product:availability" content={product && selectedVariant && selectedVariant.is_available ? "in stock" : "out of stock"} />
          
          {/* Twitter card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={product ? `${product.title} | Screamify™ Shop` : 'Official Horror Merchandise | Screamify™ Shop'} />
          <meta name="twitter:description" content="Shop official Screamify™ branded merchandise including hats, t-shirts, and clothing for the world's first indie horror streaming service." />
          <meta name="twitter:image" content={currentImage || (product?.images?.[0]?.src || "https://www.screamify.com/social-share.png")} />
          
          {/* Canonical link */}
          <link rel="canonical" href="https://www.screamify.com/shop" />
        </Helmet>
      </HelmetProvider>

      <Header />
      
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 6 }, pt: { xs: 10, sm: 16 }, flex: 1, position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ 
              color: '#ff0000',
              fontWeight: 800,
              textShadow: '0 0 15px rgba(255, 0, 0, 0.3)',
              mb: 2,
              fontSize: { xs: '2rem', sm: '3rem' },
              letterSpacing: '-0.02em',
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                width: '40%',
                height: '4px',
                background: 'linear-gradient(90deg, transparent, #ff0000, transparent)',
                bottom: '-8px',
                left: '30%'
              }
            }}
          >
            The Screamify™ Shop
          </Typography>
          
          {/* SEO text that's visually subtle but good for crawlers */}
          <Typography 
            variant="body2" 
            component="p"
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '800px',
              mx: 'auto',
              mt: 2,
              fontSize: { xs: '0.85rem', sm: '0.95rem' }
            }}
          >
            Browse our collection of official Screamify™ merchandise, including hats, t-shirts, and clothing.
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#ff0000' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
        ) : product ? (
          <Paper
            elevation={6}
            sx={{
              bgcolor: 'rgba(16, 16, 18, 0.85)',
              backdropFilter: 'blur(12px)',
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(255, 0, 0, 0.2)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 0, 0, 0.1) inset',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                boxShadow: '0 15px 50px rgba(0, 0, 0, 0.5), 0 0 25px rgba(255, 0, 0, 0.15) inset',
              }
            }}
          >
            <Grid container spacing={0} direction={{ xs: 'column', md: 'row' }}>
              {/* Product Images */}
              <Grid item xs={12} md={6}>
                 {/* ... Image display logic - ensure currentImage is used ... */}
                 <Box 
                    sx={{ 
                        p: { xs: 3, sm: 4 }, 
                        display: 'flex', 
                        flexDirection: 'column',
                        height: '100%',
                        position: 'relative',
                        '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '5%',
                        left: '5%',
                        right: '5%',
                        bottom: '5%',
                        border: '1px solid rgba(255, 0, 0, 0.1)',
                        borderRadius: 3,
                        pointerEvents: 'none',
                        opacity: 0.7,
                        zIndex: 0
                        }
                    }}
                    >
                    <Box 
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        flex: 1,
                        mb: 3,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        p: { xs: 3, sm: 4 },
                        borderRadius: 3,
                        boxShadow: 'inset 0 0 15px rgba(0, 0, 0, 0.4)',
                        position: 'relative',
                        overflow: 'hidden',
                        zIndex: 1
                        }}
                    >
                         {/* Ensure currentImage state is used here */} 
                        <Box
                            component="img" 
                            src={currentImage || screamifyLogo} // Use currentImage, fallback if needed
                            alt={product.title}
                            loading="eager" // Add eager loading for main image
                            sx={{ 
                                maxWidth: '100%', 
                                maxHeight: { xs: '250px', sm: '400px' },
                                objectFit: 'contain',
                                transition: 'transform 0.5s cubic-bezier(0.25, 0.45, 0.45, 0.95), filter 0.5s ease',
                                filter: 'drop-shadow(0 8px 20px rgba(0, 0, 0, 0.5))'
                            }}
                        />
                    </Box>
                  
                    {/* Thumbnail images */}
                    <Box 
                        sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        flexWrap: 'wrap', 
                        gap: 1.5,
                        mt: 2,
                        position: 'relative',
                        zIndex: 1
                        }}
                    >
                        {product.images.map((image, index) => (
                        <Box
                            key={index}
                            component="img"
                            src={image.src}
                            alt={`${product.title} view ${index + 1}`}
                            loading="lazy" // Add lazy loading for thumbnails
                            sx={{
                            width: { xs: 56, sm: 65 },
                            height: { xs: 56, sm: 65 },
                            objectFit: 'cover',
                            borderRadius: 2,
                            cursor: 'pointer',
                            border: currentImage === image.src 
                                ? '2px solid #ff0000' 
                                : '2px solid rgba(255, 255, 255, 0.1)',
                            transition: 'all 0.3s ease',
                            opacity: currentImage === image.src ? 1 : 0.7,
                            boxShadow: currentImage === image.src 
                                ? '0 0 10px rgba(255, 0, 0, 0.4)' 
                                : '0 0 5px rgba(0, 0, 0, 0.3)',
                            '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: '0 5px 15px rgba(0, 0, 0, 0.4)',
                                opacity: 1,
                            }
                            }}
                            onClick={() => handleImageSelect(image)}
                        />
                        ))}
                    </Box>
                    </Box>
              </Grid>
              
              {/* Product Details */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: { xs: 3, sm: 4 }, 
                  color: 'white',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: { xs: '100%', md: '1px' },
                    height: { xs: '1px', md: '100%' },
                    background: { 
                      xs: 'linear-gradient(90deg, transparent, rgba(255, 0, 0, 0.3), transparent)', 
                      md: 'linear-gradient(180deg, transparent, rgba(255, 0, 0, 0.3), transparent)' 
                    }
                  }
                }}>
                  {/* Product Title */} 
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    fontWeight="800" 
                    mb={1.5}
                    sx={{ 
                      fontSize: { xs: '1.75rem', sm: '2.25rem' },
                      letterSpacing: '-0.01em',
                      background: 'linear-gradient(90deg, #fff, #ddd)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {product.title}
                    {/* Show selected variant title if available */}
                    {selectedVariant?.title && (
                        <Typography 
                          variant="h6" 
                          component="span" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.7)', 
                            ml: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                          }}
                        >
                          ({selectedVariant.title})
                        </Typography>
                    )}
                  </Typography>
                  
                  {/* Price */} 
                   <Typography 
                    variant="h5" 
                    sx={{ 
                      color: '#ff0000', 
                      fontWeight: 'bold', 
                      mb: 2.5,
                      fontSize: { xs: '1.75rem', sm: '2rem' },
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
                    }}
                  >
                    {selectedVariant ? formatPrice(selectedVariant.price) : ''}
                    
                    {/* Removed Member Discount Info Label */}
                  </Typography>
                  
                  {/* Removed Member Discount Alert */}
                  {/* Removed Member Discount Info */}
                  {/* Removed Member Discount Alert */}
                  
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      p: 2,
                      borderRadius: 2,
                      mb: 3, 
                      lineHeight: 1.7,
                      '& p': {
                        marginBottom: '12px'
                      },
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                  
                  <Divider sx={{ my: 3, bgcolor: 'rgba(255, 255, 255, 0.08)' }} />
                  
                  {/* Color Selection - Now directly using variant.is_available */}
                  {product.options && product.options.length > 0 && (() => {
                    const colorOption = product.options.find(o => o.name === 'Colors');
                    if (!colorOption) return null;
                    
                    // Ensure we only render color options that correspond to our hardcoded variants
                    const allowedOptionIds = product.variants.map(v => v.options ? v.options[0] : null).filter(id => id);
                    const displayableColorOptions = colorOption.values.filter(opt => allowedOptionIds.includes(opt.id));

                    if (displayableColorOptions.length === 0) return null; // Don't render if no matching options
                    
                    return (
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="subtitle1" 
                          fontWeight="600" 
                          mb={1.5}
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.95)',
                            display: 'flex',
                            alignItems: 'center',
                            '&::before': {
                              content: '""',
                              display: 'inline-block',
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#ff0000',
                              mr: 1,
                              boxShadow: '0 0 8px #ff0000'
                            }
                          }}
                        >
                          Color
                        </Typography>
                        
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            gap: 1,
                            p: 1.5,
                            borderRadius: 2,
                            background: 'rgba(0, 0, 0, 0.2)',
                            boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.2)'
                          }}
                        >
                          {displayableColorOptions.map(colorOpt => {
                            // Find the hardcoded variant for this color option
                            const colorVariant = product.variants.find(v => 
                              v.options && v.options.length > 0 && v.options[0] === colorOpt.id
                            );
                            
                            // Check availability directly from the variant data
                            const colorIsAvailable = colorVariant ? colorVariant.is_available : false;
                            
                            return (
                              <Box 
                                key={colorOpt.id}
                                sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  mr: 1,
                                  mb: 1
                                }}
                              >
                                {colorOpt.colors ? (
                                  <Box 
                                    onClick={() => colorIsAvailable && handleColorSelect(colorOpt)}
                                    sx={{
                                      position: 'relative',
                                      width: 42,
                                      height: 42,
                                      cursor: colorIsAvailable ? 'pointer' : 'not-allowed',
                                      borderRadius: '50%',
                                      background: colorOpt.colors.length > 1 
                                        ? `linear-gradient(45deg, ${colorOpt.colors[0]} 50%, ${colorOpt.colors[1]} 50%)`
                                        : colorOpt.colors[0],
                                      border: selectedColor && selectedColor.id === colorOpt.id 
                                        ? '3px solid #ff0000' 
                                        : '3px solid transparent',
                                      boxShadow: selectedColor && selectedColor.id === colorOpt.id 
                                        ? '0 0 15px rgba(255, 0, 0, 0.6)' 
                                        : 'none',
                                      opacity: colorIsAvailable ? 1 : 0.35,
                                      transition: 'all 0.3s ease',
                                      '&:hover': colorIsAvailable ? {
                                        transform: 'scale(1.12)',
                                        boxShadow: '0 0 20px rgba(255, 0, 0, 0.7)'
                                      } : {},
                                      '&::after': selectedColor && selectedColor.id === colorOpt.id ? {
                                        content: '""',
                                        position: 'absolute',
                                        top: '-6px',
                                        left: '-6px',
                                        right: '-6px',
                                        bottom: '-6px',
                                        borderRadius: '50%',
                                        border: '1px solid rgba(255, 0, 0, 0.3)',
                                        animation: 'pulse 2s infinite',
                                        pointerEvents: 'none'
                                      } : {}
                                    }}
                                  />
                                ) : null}
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: '12px', 
                                    mt: 1,
                                    color: colorIsAvailable ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.5)',
                                    fontWeight: 500,
                                    maxWidth: '60px',
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    position: 'relative'
                                  }}
                                >
                                  {colorOpt.title}
                                  {!colorIsAvailable && (
                                    <Typography 
                                      component="span" 
                                      sx={{ 
                                        display: 'block',
                                        fontSize: '10px',
                                        color: '#ff5252'
                                      }}
                                    >
                                      (Out of stock)
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })()}
                  
                  {/* Quantity Selection */}
                  <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="600" 
                      mr={2} 
                      sx={{ 
                        width: { xs: '100%', sm: 'auto' }, 
                        mb: { xs: 1.5, sm: 0 },
                        color: 'rgba(255, 255, 255, 0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        '&::before': {
                          content: '""',
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#ff0000',
                          mr: 1,
                          boxShadow: '0 0 8px #ff0000'
                        }
                      }}
                    >
                      Quantity
                    </Typography>
                    
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: 2,
                        overflow: 'hidden',
                        width: { xs: '160px', sm: 'auto' },
                        mx: { xs: 'auto', sm: 0 },
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        background: 'linear-gradient(to bottom, rgba(40, 40, 40, 0.8), rgba(20, 20, 20, 0.8))'
                      }}
                    >
                      <IconButton 
                        size="medium" 
                        onClick={() => handleQuantityChange(quantity - 1)}
                        disabled={quantity <= 1}
                        sx={{ 
                          color: 'white',
                          p: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                          },
                          '&.Mui-disabled': {
                            color: 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                      
                      <TextField
                        value={quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val)) {
                            handleQuantityChange(val);
                          }
                        }}
                        InputProps={{
                          sx: {
                            input: {
                              textAlign: 'center',
                              color: 'white',
                              width: '45px',
                              p: 1,
                              fontWeight: 600,
                              fontSize: '1.1rem'
                            },
                            '& fieldset': {
                              border: 'none'
                            }
                          }
                        }}
                      />
                      
                      <IconButton 
                        size="medium" 
                        onClick={() => handleQuantityChange(quantity + 1)}
                        disabled={quantity >= 10}
                        sx={{ 
                          color: 'white',
                          p: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                          },
                          '&.Mui-disabled': {
                            color: 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Purchase Button */}
                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={handleInitiatePayment}
                      disabled={
                        isSubmitting || 
                        !selectedVariant ||
                        !selectedVariant.is_available // Directly check the selected variant's availability
                      }
                      sx={{
                        bgcolor: '#ff0000',
                        color: 'white',
                        py: { xs: 1.5, sm: 2 },
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(255, 0, 0, 0.4)',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: '#d10000',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 25px rgba(255, 0, 0, 0.5)'
                        },
                        '&:active': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 15px rgba(255, 0, 0, 0.4)'
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: '-50%',
                          left: '-50%',
                          right: '-50%',
                          bottom: '-50%',
                          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.13) 77%, rgba(255, 255, 255, 0.5) 92%, rgba(255, 255, 255, 0.3) 100%)',
                          opacity: 0,
                          transform: 'rotate(30deg)',
                          transition: 'opacity 0.4s ease',
                        },
                        '&:hover::after': {
                          opacity: 1,
                          animation: 'shine 1.5s ease',
                        }
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={26} sx={{ color: 'white' }} />
                      ) : (
                        <>
                          Purchase - {selectedVariant ? formatPrice(selectedVariant.price * quantity) : ''} + Tax
                        </>
                      )}
                    </Button>
                    
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block', 
                        textAlign: 'center', 
                        mt: 1.5, 
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.8rem' 
                      }}
                    >
                      Secure payment • Fast shipping • Money-back guarantee
                    </Typography>
                  </Box>
                  
                  {/* Order success message */}
                  {orderPlaced && (
                    <Box sx={{ my: 4, textAlign: 'center' }}>
                      <Alert severity="success" sx={{ 
                        mb: 3, 
                        py: 2,
                        bgcolor: 'rgba(46, 125, 50, 0.15)',
                        color: 'white',
                        '& .MuiAlert-icon': {
                          color: '#4caf50'
                        },
                        borderRadius: 2,
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
                      }}>
                        <AlertTitle sx={{ fontSize: '1.2rem', fontWeight: 600 }}>Order Successfully Placed!</AlertTitle>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          Your order from The Screamify™ Shop is on the way.
                        </Typography>
                      </Alert>
                      
                      <Button 
                        variant="contained" 
                        onClick={() => setOrderPlaced(false)} 
                        sx={{ 
                          mt: 2,
                          bgcolor: '#ff0000',
                          color: 'white',
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 600,
                          boxShadow: '0 4px 15px rgba(255, 0, 0, 0.3)',
                          '&:hover': {
                            bgcolor: '#d10000',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 18px rgba(255, 0, 0, 0.4)'
                          }
                        }}
                      >
                        Place Another Order
                      </Button>
                    </Box>
                  )}
                  
                  {/* Final check for unavailability (using variant data) */}
                  {selectedVariant && !selectedVariant.is_available ? (
                    <Alert severity="warning" sx={{ 
                      mt: 2, 
                      borderRadius: 2,
                      bgcolor: 'rgba(237, 108, 2, 0.15)',
                      color: 'white',
                      border: '1px solid rgba(237, 108, 2, 0.3)',
                      '& .MuiAlert-icon': {
                        color: '#ed6c02'
                      }
                    }}>
                      {`This ${selectedVariant?.title || 'product'} is currently out of stock`}
                    </Alert>
                  ) : null}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Alert severity="info" sx={{ 
            borderRadius: 2,
            bgcolor: 'rgba(2, 136, 209, 0.15)',
            color: 'white',
            border: '1px solid rgba(2, 136, 209, 0.3)',
            '& .MuiAlert-icon': {
              color: '#0288d1'
            }
          }}>
            No products found
          </Alert>
        )}
        
        {/* Add some additional SEO text at the bottom of the page */}
        {!loading && !error && product && (
          <Box mt={6} mb={4} px={2} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            <Typography variant="h2" component="h2" sx={{ fontSize: '1.5rem', mb: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
              About Screamify™ Merchandise
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              The Screamify™ Shop offers high-quality, official merchandise from Screamify™. 
              Our collection includes premium hats, t-shirts, and clothing items designed for fans.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Our merchandise features unique designs and premium quality materials for comfort and durability.
            </Typography>
            <Typography variant="body2">
              By purchasing Screamify™ branded items, you're getting awesome merchandise to show your support for the brand.
            </Typography>
          </Box>
        )}
      </Container>
      
      <Footer />
      
      {/* Payment Modal */}
      <Modal
        open={paymentModalOpen}
        onClose={handleClosePaymentModal}
        aria-labelledby="payment-modal-title"
        closeAfterTransition
        sx={{
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }}
      >
        <Paper sx={{
          ...modalStyle,
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: paymentModalOpen 
            ? 'translate(-50%, -50%) scale(1)' 
            : 'translate(-50%, -50%) scale(0.95)',
          opacity: paymentModalOpen ? 1 : 0,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography 
              id="payment-modal-title" 
              variant="h5" 
              component="h2" 
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 600,
                color: '#212121'
              }}
            >
              Complete Your Purchase
            </Typography>
            <IconButton 
              onClick={handleClosePaymentModal}
              disabled={paymentProcessing}
              sx={{ 
                color: 'gray',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  color: '#000'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          {/* Order Summary */}
          {paymentOrderData && (
            <Box sx={{ 
              mb: 3, 
              p: 2.5, 
              bgcolor: 'rgba(0, 0, 0, 0.03)', 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: 'inset 0 0 8px rgba(0, 0, 0, 0.02)'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#212121' }}>
                Order Summary
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: 500, fontSize: '1rem', color: '#424242' }}>
                    {paymentOrderData.title} {paymentOrderData.selectedVariant.title && `- ${paymentOrderData.selectedVariant.title}`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Quantity:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right" fontWeight={500}>
                    {paymentOrderData.quantity}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Price per item:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right" fontWeight={500}>
                    ${paymentOrderData.price.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right" fontWeight={500}>
                    ${(paymentOrderData.subtotalCents / 100).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Sales Tax ({paymentOrderData.taxRatePercent}%):
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right" fontWeight={500}>
                    ${(paymentOrderData.taxCents / 100).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#212121' }}>
                    Total:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" align="right" sx={{ fontWeight: 700, color: '#ff0000' }}>
                    ${paymentOrderData.totalPrice.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm 
                clientSecret={clientSecret}
                orderData={paymentOrderData}
                onPaymentComplete={handlePaymentComplete}
                onCancel={handleClosePaymentModal}
              />
            </Elements>
          )}
          
          {paymentError && (
            <Alert severity="error" sx={{ 
              mt: 2,
              border: '1px solid rgba(211, 47, 47, 0.2)',
              borderRadius: 1.5
            }}>
              {paymentError}
            </Alert>
          )}
        </Paper>
      </Modal>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Shop;